import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight,
  Truck,
  Coffee,
  UtensilsCrossed,
  Wine,
  Building2,
  Briefcase,
  TrendingUp,
  TrendingDown,
  Zap,
  Star,
  Bell,
  PieChart,
  Users,
  Receipt,
  Timer,
  MapPin,
  BarChart3,
  FileBarChart,
  Wallet,
  Bike,
  CalendarClock,
  Trash2,
  Megaphone,
  GitCompareArrows,
} from 'lucide-react';
import SEO from '../components/SEO';
import ContinueExploring from '../components/ContinueExploring';
import { useT } from '../i18n';

/* Dashboards — interactive gallery of live dashboard examples per
   hospitality segment: food truck → café → full-service → bar →
   multi-location group → catering. All mock data, all animated. */

function Tick({ base, step = 1, every = 2600, prefix = '', suffix = '' }) {
  const [n, setN] = useState(base);
  useEffect(() => {
    setN(base);
    const t = setInterval(() => setN((v) => v + Math.ceil(Math.random() * step)), every);
    return () => clearInterval(t);
  }, [base, step, every]);
  return <>{prefix}{n.toLocaleString()}{suffix}</>;
}

export const SEGMENTS = (es) => [
  {
    key: 'foodtruck',
    icon: Truck,
    label: 'Food Truck',
    tag: es ? '1 unidad móvil' : '1 mobile unit',
    headline: es ? 'Dónde estacionarte mañana para vender más.' : 'Where to park tomorrow to sell more.',
    kpis: [
      { t: es ? 'Ventas hoy' : 'Sales today', v: <Tick base={1240} step={18} prefix="$" />, trend: '+14%', up: true },
      { t: es ? 'Ticket promedio' : 'Avg ticket', v: '$16.40', trend: '+$1.20', up: true },
      { t: es ? 'Mejor ubicación' : 'Best spot', v: 'Downtown', trend: es ? 'Vie–Sáb' : 'Fri–Sat', up: true },
      { t: es ? 'Tiempo de espera' : 'Wait time', v: '4.2 min', trend: '-38s', up: true },
    ],
    bars: [35, 48, 42, 66, 90, 84, 58],
    items: [
      { n: es ? 'Tacos al pastor (x3)' : 'Al pastor tacos (x3)', p: 82 },
      { n: es ? 'Quesabirria' : 'Quesabirria', p: 71 },
      { n: 'Agua fresca', p: 44 },
    ],
    itemsLabel: es ? 'Top del día' : "Today's top sellers",
    insight: es
      ? 'Insight: los viernes en Downtown vendes 2.1× más que en el parque industrial. Reserva ese spot.'
      : 'Insight: Fridays downtown you sell 2.1× more than at the industrial park. Lock that spot.',
    alert: es ? 'Stock de tortillas al 15% — pedir antes de las 3 PM' : 'Tortilla stock at 15% — reorder before 3 PM',
  },
  {
    key: 'cafe',
    icon: Coffee,
    label: es ? 'Café / QSR' : 'Café / QSR',
    tag: es ? 'Servicio rápido' : 'Quick service',
    headline: es ? 'La hora pico, medida al minuto.' : 'Your rush hour, measured to the minute.',
    kpis: [
      { t: es ? 'Ventas hoy' : 'Sales today', v: <Tick base={2860} step={22} prefix="$" />, trend: '+9%', up: true },
      { t: es ? 'Órdenes / hora pico' : 'Orders / peak hr', v: <Tick base={94} step={1} every={4000} />, trend: '+11', up: true },
      { t: es ? 'Velocidad de servicio' : 'Speed of service', v: '2:47', trend: '-14s', up: true },
      { t: es ? 'Clientes que repiten' : 'Repeat guests', v: '38%', trend: '+5 pts', up: true },
    ],
    bars: [30, 85, 92, 55, 40, 62, 74],
    items: [
      { n: 'Latte 16oz', p: 88 },
      { n: es ? 'Croissant de jamón' : 'Ham croissant', p: 64 },
      { n: 'Cold brew', p: 52 },
    ],
    itemsLabel: es ? 'Más vendidos AM' : 'AM best sellers',
    insight: es
      ? 'Insight: el combo latte + croissant sube el ticket 22%. Sugerirlo en pantalla a las 7–10 AM.'
      : 'Insight: the latte + croissant combo lifts ticket 22%. Suggest it on screen 7–10 AM.',
    alert: es ? 'Cola > 6 personas detectada a las 8:12 — abrir segunda caja' : 'Queue > 6 people at 8:12 — open second register',
  },
  {
    key: 'fullservice',
    icon: UtensilsCrossed,
    label: es ? 'Restaurante' : 'Full-Service',
    tag: es ? 'Servicio completo' : 'Full service',
    headline: es ? 'Margen por platillo, ocupación por turno.' : 'Margin per dish, occupancy per shift.',
    kpis: [
      { t: es ? 'Ventas hoy' : 'Sales today', v: <Tick base={6420} step={35} prefix="$" />, trend: '+12%', up: true },
      { t: es ? 'Ocupación cena' : 'Dinner occupancy', v: '87%', trend: '+6 pts', up: true },
      { t: es ? 'Costo de alimentos' : 'Food cost', v: '29.4%', trend: '-1.1 pts', up: true },
      { t: es ? 'Rating promedio' : 'Avg rating', v: '4.7★', trend: '+0.2', up: true },
    ],
    bars: [40, 44, 52, 60, 78, 95, 88],
    items: [
      { n: es ? 'Rib-eye 12oz · margen 68%' : 'Rib-eye 12oz · 68% margin', p: 84 },
      { n: es ? 'Pasta trufa · margen 74%' : 'Truffle pasta · 74% margin', p: 76 },
      { n: es ? 'Ceviche · margen 61%' : 'Ceviche · 61% margin', p: 58 },
    ],
    itemsLabel: es ? 'Estrellas del menú (margen)' : 'Menu stars (by margin)',
    insight: es
      ? 'Insight: el 12% de tu menú genera el 61% de tu utilidad. Tres platillos deberían salir de la carta.'
      : 'Insight: 12% of your menu drives 61% of your profit. Three dishes should leave the card.',
    alert: es ? 'Mermas de salmón +18% esta semana vs. promedio' : 'Salmon waste +18% this week vs. average',
  },
  {
    key: 'bar',
    icon: Wine,
    label: es ? 'Bar / Nightlife' : 'Bar / Nightlife',
    tag: es ? 'Alto volumen nocturno' : 'High-volume nights',
    headline: es ? 'Pour cost bajo control, noche a noche.' : 'Pour cost under control, night after night.',
    kpis: [
      { t: es ? 'Ventas anoche' : 'Last night sales', v: '$9,340', trend: '+21%', up: true },
      { t: 'Pour cost', v: '19.8%', trend: '-2.3 pts', up: true },
      { t: es ? 'Ticket por persona' : 'Spend per head', v: '$42', trend: '+$6', up: true },
      { t: es ? 'Hora pico' : 'Peak hour', v: '11 PM', trend: es ? 'Sáb' : 'Sat', up: true },
    ],
    bars: [20, 25, 35, 55, 80, 96, 70],
    items: [
      { n: 'Mezcal old fashioned', p: 90 },
      { n: 'Espresso martini', p: 77 },
      { n: es ? 'Cerveza artesanal' : 'Craft beer', p: 49 },
    ],
    itemsLabel: es ? 'Cócteles top del fin' : 'Weekend top cocktails',
    insight: es
      ? 'Insight: el happy hour de jueves trae 40% más clientes pero baja el pour cost solo 1 pt — es rentable, extiéndelo.'
      : 'Insight: Thursday happy hour brings 40% more guests and only 1pt pour-cost hit — profitable, extend it.',
    alert: es ? 'Varianza de inventario en vodka premium: -4 botellas' : 'Inventory variance on premium vodka: -4 bottles',
  },
  {
    key: 'group',
    icon: Building2,
    label: es ? 'Grupo Multi-sede' : 'Multi-Location',
    tag: es ? '5–200 sedes' : '5–200 locations',
    headline: es ? 'Todas tus sedes, un solo ranking en vivo.' : 'Every location, one live ranking.',
    kpis: [
      { t: es ? 'Ventas del grupo' : 'Group sales', v: <Tick base={48200} step={120} prefix="$" />, trend: '+8%', up: true },
      { t: es ? 'Mejor sede' : 'Top location', v: 'Palo Alto', trend: '+19%', up: true },
      { t: es ? 'Sede en riesgo' : 'At-risk location', v: 'San José', trend: '-7%', up: false },
      { t: es ? 'Labor cost grupo' : 'Group labor cost', v: '26.1%', trend: '-0.8 pts', up: true },
    ],
    bars: [70, 82, 64, 45, 76, 88, 92],
    items: [
      { n: 'Palo Alto · $12,400', p: 92 },
      { n: 'Mountain View · $10,100', p: 75 },
      { n: 'San José · $7,900', p: 54 },
    ],
    itemsLabel: es ? 'Ranking de sedes (hoy)' : 'Location ranking (today)',
    insight: es
      ? 'Insight: San José cae 7% por reseñas de servicio lento. El mismo menú en Palo Alto crece 19%. El problema es operativo, no de producto.'
      : 'Insight: San José is down 7% on slow-service reviews. Same menu in Palo Alto grows 19%. The problem is ops, not product.',
    alert: es ? 'Paridad de precios rota: 3 ítems difieren entre sedes' : 'Price parity broken: 3 items differ across locations',
  },
  {
    key: 'catering',
    icon: Briefcase,
    label: 'Catering',
    tag: es ? 'Eventos y corporativo' : 'Events & corporate',
    headline: es ? 'Tu pipeline de eventos, como un CRM de ventas.' : 'Your event pipeline, run like a sales CRM.',
    kpis: [
      { t: es ? 'Pipeline activo' : 'Active pipeline', v: '$86,500', trend: '+23%', up: true },
      { t: es ? 'Eventos este mes' : 'Events this month', v: '14', trend: '+3', up: true },
      { t: es ? 'Tasa de cierre' : 'Close rate', v: '34%', trend: '+6 pts', up: true },
      { t: es ? 'Clientes que repiten' : 'Repeat buyers', v: '2.3×', trend: es ? 'al año' : 'per year', up: true },
    ],
    bars: [45, 52, 38, 65, 72, 60, 85],
    items: [
      { n: es ? 'Tech Corp · 120 pax · $8,400' : 'Tech Corp · 120 ppl · $8,400', p: 88 },
      { n: es ? 'Boda García · 80 pax · $6,200' : 'García wedding · 80 ppl · $6,200', p: 66 },
      { n: es ? 'Univ. Stanford · 60 pax · $3,900' : 'Stanford Univ · 60 ppl · $3,900', p: 45 },
    ],
    itemsLabel: es ? 'Cotizaciones abiertas' : 'Open quotes',
    insight: es
      ? 'Insight: los compradores corporativos que repiten en 60 días valen 4× más. Dos cuentas están listas para el follow-up.'
      : 'Insight: corporate buyers who reorder within 60 days are worth 4× more. Two accounts are ready for follow-up.',
    alert: es ? 'Cotización de Tech Corp sin respuesta hace 5 días' : 'Tech Corp quote unanswered for 5 days',
  },
];

const REPORTS = (es) => [
  { icon: Wallet, t: es ? 'P&L diario' : 'Daily P&L', b: es ? 'Ingresos, costos y margen de ayer, en tu teléfono antes del café.' : "Yesterday's revenue, costs and margin — on your phone before coffee." },
  { icon: PieChart, t: es ? 'Ingeniería de menú' : 'Menu engineering', b: es ? 'Qué platillo es estrella, cuál es caballo de trabajo y cuál hay que eliminar.' : 'Which dish is a star, which is a workhorse, and which must go.' },
  { icon: Users, t: es ? 'Costo laboral' : 'Labor cost', b: es ? 'Horas vs. ventas por turno. Detecta sobre-staffing antes de la quincena.' : 'Hours vs. sales per shift. Catch over-staffing before payday.' },
  { icon: Bike, t: es ? 'Mix de canales delivery' : 'Delivery channel mix', b: es ? 'DoorDash vs. Uber Eats vs. directo — comisión real y margen neto por canal.' : 'DoorDash vs. Uber Eats vs. direct — true commission and net margin per channel.' },
  { icon: Star, t: es ? 'Reputación y reseñas' : 'Reputation & reviews', b: es ? 'Rating, velocidad de respuesta y temas recurrentes en Google, Yelp y TripAdvisor.' : 'Rating, response speed and recurring themes across Google, Yelp and TripAdvisor.' },
  { icon: CalendarClock, t: es ? 'Ocupación y reservas' : 'Occupancy & reservations', b: es ? 'Cobertura por turno, no-shows y pronóstico del fin de semana.' : 'Covers per shift, no-shows and the weekend forecast.' },
  { icon: Timer, t: es ? 'Velocidad de servicio' : 'Speed of service', b: es ? 'Ticket-to-table por daypart. Los minutos que cuestan mesas.' : 'Ticket-to-table by daypart. The minutes that cost you tables.' },
  { icon: Trash2, t: es ? 'Merma e inventario' : 'Waste & inventory', b: es ? 'Varianza teórico vs. real por insumo. La fuga silenciosa del margen.' : 'Theoretical vs. actual variance per ingredient. The silent margin leak.' },
  { icon: Megaphone, t: 'Marketing ROI', b: es ? 'Cada campaña, promo y post — atribuida a ventas reales del POS.' : 'Every campaign, promo and post — attributed to real POS sales.' },
  { icon: GitCompareArrows, t: es ? 'Comparativa entre sedes' : 'Location benchmarking', b: es ? 'Mismo estándar para todas: ventas, labor, reseñas y merma, lado a lado.' : 'One standard for all: sales, labor, reviews and waste, side by side.' },
  { icon: Briefcase, t: es ? 'Pipeline de catering' : 'Catering pipeline', b: es ? 'Cotizaciones, cierres y recompra corporativa como un CRM de ventas.' : 'Quotes, closes and corporate reorders — run like a sales CRM.' },
  { icon: Receipt, t: es ? 'Flujo de caja' : 'Cash flow', b: es ? 'Cobros de plataformas, pagos a proveedores y proyección a 30 días.' : 'Platform payouts, supplier payments and a 30-day projection.' },
];

function KpiCard({ k }) {
  return (
    <div className="rounded-xl bg-white/[0.05] border border-white/10 p-3.5">
      <p className="text-[9.5px] uppercase tracking-wider text-neutral-500 font-bold truncate">{k.t}</p>
      <p className="mt-1 text-[17px] font-bold text-white truncate">{k.v}</p>
      <p className={`mt-0.5 inline-flex items-center gap-1 text-[10.5px] font-semibold ${k.up ? 'text-emerald-400' : 'text-red-400'}`}>
        {k.up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />} {k.trend}
      </p>
    </div>
  );
}

export function SegmentDashboard({ seg, es }) {
  return (
    <motion.div
      key={seg.key}
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.35 }}
      className="rounded-3xl bg-white/[0.04] border border-white/10 p-6 sm:p-8 backdrop-blur"
      data-testid={`dash-demo-${seg.key}`}
    >
      <div className="flex flex-wrap items-center gap-3">
        <span className="inline-flex w-10 h-10 rounded-xl bg-[#F77F11]/15 border border-[#F77F11]/30 items-center justify-center">
          <seg.icon className="w-5 h-5 text-[#FFB672]" />
        </span>
        <div className="min-w-0">
          <h3 className="font-bold text-[17px] tracking-[-0.005em] text-white">{seg.headline}</h3>
          <p className="text-[11.5px] text-neutral-500">{seg.label} · {seg.tag}</p>
        </div>
        <span className="ml-auto inline-flex items-center gap-1.5 text-[10.5px] font-semibold text-emerald-400">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> LIVE
        </span>
      </div>

      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3">
        {seg.kpis.map((k) => <KpiCard key={k.t} k={k} />)}
      </div>

      <div className="mt-5 grid lg:grid-cols-2 gap-5">
        {/* Weekly bars */}
        <div className="rounded-2xl bg-black/25 border border-white/10 p-5">
          <p className="text-[10px] font-bold tracking-[0.18em] uppercase text-neutral-500">
            {es ? 'Ventas · últimos 7 días' : 'Sales · last 7 days'}
          </p>
          <div className="mt-4 flex items-end gap-2 h-28">
            {seg.bars.map((h, i) => (
              <motion.div
                key={`${seg.key}-${i}`}
                className={`flex-1 rounded-t-md ${i === seg.bars.length - 1 ? 'bg-[#F77F11]' : 'bg-white/15'}`}
                initial={{ height: 0 }}
                animate={{ height: `${h}%` }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
              />
            ))}
          </div>
          <div className="mt-2 flex justify-between text-[9px] text-neutral-600 font-semibold uppercase">
            {(es ? ['L', 'M', 'M', 'J', 'V', 'S', 'D'] : ['M', 'T', 'W', 'T', 'F', 'S', 'S']).map((d, i) => <span key={i}>{d}</span>)}
          </div>
        </div>

        {/* Top list */}
        <div className="rounded-2xl bg-black/25 border border-white/10 p-5">
          <p className="text-[10px] font-bold tracking-[0.18em] uppercase text-neutral-500">{seg.itemsLabel}</p>
          <div className="mt-4 space-y-3.5">
            {seg.items.map((it, i) => (
              <div key={it.n}>
                <div className="flex items-center justify-between text-[12.5px]">
                  <span className="text-neutral-200 font-medium truncate pr-3">{it.n}</span>
                  <span className="text-neutral-500 font-semibold shrink-0">{it.p}%</span>
                </div>
                <div className="mt-1.5 h-1.5 rounded-full bg-white/10 overflow-hidden">
                  <motion.div
                    className="h-full rounded-full bg-gradient-to-r from-[#F77F11] to-[#ff9b4a]"
                    initial={{ width: 0 }}
                    animate={{ width: `${it.p}%` }}
                    transition={{ duration: 0.6, delay: 0.15 + i * 0.1 }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-5 grid sm:grid-cols-2 gap-3">
        <div className="flex items-start gap-2.5 rounded-xl bg-emerald-400/10 border border-emerald-400/25 px-4 py-3">
          <Zap className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
          <p className="text-[12px] text-neutral-200 leading-relaxed">{seg.insight}</p>
        </div>
        <div className="flex items-start gap-2.5 rounded-xl bg-red-400/10 border border-red-400/25 px-4 py-3">
          <Bell className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
          <p className="text-[12px] text-neutral-200 leading-relaxed">
            <span className="font-bold text-red-300 text-[10px] tracking-[0.14em] uppercase mr-1.5">{es ? 'Alerta' : 'Alert'}</span>
            {seg.alert}
          </p>
        </div>
      </div>
    </motion.div>
  );
}

export default function DashboardsPage() {
  const { lang } = useT();
  const es = lang === 'es';
  const segments = SEGMENTS(es);
  const [active, setActive] = useState('fullservice');
  const seg = segments.find((s) => s.key === active);
  const reports = REPORTS(es);

  return (
    <div className="bg-white" data-testid="dashboards-page">
      <SEO
        title={es ? 'Dashboards para Restaurantes | Kitxens' : 'Restaurant Dashboards | Kitxens'}
        description={es
          ? 'Dashboards en vivo diseñados para hospitalidad: food trucks, cafés, restaurantes, bares, grupos multi-sede y catering. Ejemplos interactivos de cada reporte que tu operación necesita.'
          : 'Live dashboards built for hospitality: food trucks, cafés, full-service restaurants, bars, multi-location groups and catering. Interactive examples of every report your operation needs.'}
        path="/services/dashboards"
      />

      {/* ═══════ HERO + INTERACTIVE DEMO (dark) ═══════ */}
      <section className="relative bg-[#0B0D12] text-white pt-28 pb-20 lg:pt-32 lg:pb-24 overflow-hidden">
        <div className="pointer-events-none absolute -top-32 right-0 w-[520px] h-[520px] rounded-full bg-[#F77F11]/[0.12] blur-3xl" />
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 relative">
          <div className="max-w-3xl">
            <Link to="/services/data-analytics" className="inline-flex items-center gap-1 text-[11px] tracking-[0.2em] font-bold uppercase text-[#F77F11] hover:text-[#FFB672]">
              ← Data & Analytics
            </Link>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-[-0.02em] leading-[1.05]">
              {es ? 'Un dashboard para cada tipo de operación.' : 'A dashboard for every kind of operation.'}
            </h1>
            <p className="mt-5 text-neutral-300 text-lg leading-relaxed max-w-2xl">
              {es
                ? 'Desde un food truck hasta un grupo con 40 sedes: diseñamos el panel exacto que tu operación necesita. Elige tu tipo de negocio y mira un ejemplo real funcionando.'
                : 'From a single food truck to a 40-location group: we design the exact dashboard your operation needs. Pick your business type and see a real example running.'}
            </p>
          </div>

          {/* Segment selector */}
          <div className="mt-10 flex flex-wrap gap-2.5" role="tablist">
            {segments.map((s) => (
              <button
                key={s.key}
                onClick={() => setActive(s.key)}
                role="tab"
                aria-selected={active === s.key}
                className={`inline-flex items-center gap-2 rounded-full border px-4 py-2.5 text-[13.5px] font-semibold transition-all ${
                  active === s.key
                    ? 'bg-[#F77F11] border-[#F77F11] text-white shadow-[0_12px_35px_-12px_rgba(247,127,17,0.7)]'
                    : 'bg-white/[0.04] border-white/15 text-neutral-300 hover:border-[#F77F11]/60 hover:text-white'
                }`}
                data-testid={`dash-tab-${s.key}`}
              >
                <s.icon className="w-4 h-4" /> {s.label}
              </button>
            ))}
          </div>

          <div className="mt-6">
            <AnimatePresence mode="wait">
              <SegmentDashboard seg={seg} es={es} />
            </AnimatePresence>
          </div>

          <p className="mt-5 text-[12.5px] text-neutral-500">
            {es
              ? '* Datos de demostración. Tu dashboard se conecta a tu POS, delivery, reservas y Google reales.'
              : '* Demo data. Your dashboard connects to your real POS, delivery, reservations and Google.'}
          </p>
        </div>
      </section>

      {/* ═══════ EVERY REPORT ═══════ */}
      <section className="py-16 lg:py-24">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
          <div className="max-w-2xl">
            <p className="kx-eyebrow">{es ? 'Todo tipo de reporte' : 'Every report type'}</p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-neutral-900 tracking-tight leading-tight">
              {es ? 'Si le importa a un restaurantero, lo medimos.' : 'If it matters to an operator, we measure it.'}
            </h2>
            <p className="mt-4 text-neutral-600 text-[15.5px] leading-relaxed">
              {es
                ? 'Estos son los reportes que ya construimos para operaciones de hospitalidad. Cada uno vive en tu dashboard, se actualiza solo y termina en una recomendación — no en una hoja de cálculo.'
                : 'These are the reports we already build for hospitality operations. Each one lives in your dashboard, updates itself, and ends in a recommendation — not a spreadsheet.'}
            </p>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {reports.map((r, i) => (
              <motion.div
                key={r.t}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.35, delay: (i % 3) * 0.05 }}
                className="group rounded-2xl border border-neutral-200 bg-white p-6 hover:border-[#F77F11] hover:shadow-[0_20px_60px_-30px_rgba(247,127,17,0.4)] transition-all"
                data-testid={`dash-report-${i}`}
              >
                <span className="inline-flex w-10 h-10 rounded-lg bg-orange-50 items-center justify-center group-hover:bg-[#F77F11] transition-colors">
                  <r.icon className="w-5 h-5 text-[#F77F11] group-hover:text-white transition-colors" />
                </span>
                <h3 className="mt-4 font-bold text-neutral-900 text-[16px] tracking-[-0.005em]">{r.t}</h3>
                <p className="mt-1.5 text-neutral-600 text-[13.5px] leading-relaxed">{r.b}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ HOW / PRINCIPLES ═══════ */}
      <section className="py-16 lg:py-20 bg-neutral-50 border-y border-neutral-200">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-3 gap-5">
            {[
              { icon: BarChart3, t: es ? '5 métricas por rol' : '5 metrics per role', b: es ? 'El dueño ve margen. El gerente ve turnos. La cocina ve tiempos. Nadie se ahoga en datos que no le sirven.' : 'The owner sees margin. The manager sees shifts. The kitchen sees timing. No one drowns in data they can\'t use.' },
              { icon: Zap, t: es ? 'Actualización < 5 min' : 'Updates in < 5 min', b: es ? 'Conectado directo a tu POS, delivery, reservas y Google. Sin exportar nada, nunca.' : 'Connected straight to your POS, delivery, reservations and Google. Never export anything, ever.' },
              { icon: FileBarChart, t: es ? 'Co-diseñado contigo' : 'Co-designed with you', b: es ? 'La primera versión la construimos juntos en la semana 1. Luego iteramos cada trimestre con tu equipo.' : 'We build the first version together in week one. Then we iterate quarterly with your team.' },
            ].map(({ icon: Icon, t, b }) => (
              <div key={t} className="rounded-2xl border border-neutral-200 bg-white p-6">
                <span className="inline-flex w-11 h-11 rounded-xl bg-neutral-950 text-white items-center justify-center"><Icon className="w-5 h-5" /></span>
                <h3 className="mt-4 font-bold text-neutral-900 text-[16px]">{t}</h3>
                <p className="mt-2 text-[13.5px] text-neutral-600 leading-relaxed">{b}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Link to="/contact?type=discovery" className="inline-flex items-center gap-2 rounded-full bg-[#F77F11] hover:bg-[#d8651a] text-white px-7 h-12 font-semibold text-[15px] transition-colors" data-testid="dash-cta-discovery">
              {es ? 'Diseñemos tu dashboard' : "Let's design your dashboard"} <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/pricing" className="inline-flex items-center gap-2 rounded-full border border-neutral-300 hover:border-[#F77F11] hover:text-[#F77F11] text-neutral-800 px-7 h-12 font-semibold text-[15px] transition-colors" data-testid="dash-cta-pricing">
              {es ? 'Incluido desde $99/mes' : 'Included from $99/mo'}
            </Link>
            <span className="inline-flex items-center gap-2 text-[13px] text-neutral-500">
              <MapPin className="w-4 h-4 text-[#F77F11]" />
              {es ? 'De food trucks a grupos restauranteros' : 'From food trucks to restaurant groups'}
            </span>
          </div>
        </div>
      </section>

      <ContinueExploring context="dashboards" />
    </div>
  );
}
